import { ComponentType, useId } from 'react';

import { twMerge } from 'tailwind-merge';
import { LinkOptions, useLocation, useRouter } from '@tanstack/react-router';
import { Typography } from '../../ui';
import { NotificationCountStyleVariants } from '../../atoms/NotificationCount/variants';
import { Tab, TabList, Tabs } from 'react-aria-components';
import { Divider } from '../../atoms/divider/divider';

type Props = {
  links: Array<Omit<SubnavItemProps, 'position' | 'isLast'>>;
  className?: string;
} & Omit<ComponentType<typeof Tabs>, 'className'>;

export function Stepper(props: Props) {
  const id = useId();
  const { links, className, ...rest } = props;
  const { pathname } = useLocation();

  return (
    <Tabs
      selectedKey={pathname}
      className={twMerge('flex flex-col px-32 py-12', className)}
      {...rest}
    >
      <TabList className="flex space-x-12" aria-label="Sub Navigation">
        {links.map((item, index, all) => (
          <SubnavItem
            key={`subnav-${id}-item-${item.to}`}
            {...item}
            className={index === all.length - 2 ? 'flex-1' : ''}
            isLast={index === all.length - 1}
            position={index + 1}
          />
        ))}
      </TabList>
    </Tabs>
  );
}

type SubnavItemProps = {
  label: string;
  position: number;
  notification?: {
    count: number;
    intent: NotificationCountStyleVariants['intent'];
  };
  className?: string;
  isLast?: boolean;
} & LinkOptions;

function SubnavItem(props: SubnavItemProps) {
  const { label, notification, isLast, position, ...rest } = props;
  const { pathname } = useLocation();
  const router = useRouter();
  const resolvedLink = router.buildLocation({
    to: props.to,
    params: props.params,
  });

  const isActive = pathname === resolvedLink.href;

  return (
    <Tab
      id={rest.to}
      href={rest.to}
      className={twMerge(
        'transition-colors duration-300 ease-out outline-none rounded-4',
        isActive && 'text-primaryMain bg-grayscale0',
        !isActive && "data-[focus-visible='true']:bg-grayscale100",
        rest.className,
      )}
    >
      <div className="flex flex-row items-center space-x-8">
        <div
          className={twMerge(
            'flex items-center justify-center min-w-40 w-40 h-40 rounded-6',
            isActive && 'bg-primaryMain',
            !isActive && 'bg-primary50',
          )}
        >
          <Typography
            variant="body-m"
            weight="medium"
            className={twMerge(
              isActive && 'text-grayscale0',
              !isActive && 'text-primaryMain',
            )}
          >
            {position}
          </Typography>
        </div>
        <Typography
          variant="body-s"
          weight="medium"
          className="text-primaryMain"
        >
          {label}
        </Typography>

        {!isLast && <Divider className="flex-1" />}
      </div>
    </Tab>
  );
}
